import { getPracticeAssessmentCollectionPath } from '../../collections/collections/assessments'
import {
  APPOINTMENTS,
  ASSESSMENTS,
  ASSESSMENTS_ADMIN,
  CONSENT_FORMS,
  MIDWIVES,
  PRACTICE_CONTRACTS,
} from '../../collections/names'
import { PopulatedAssessment } from '../../types/app/populated'
import {
  AdminAssessmentData,
  Assessment,
  AssessmentSnippet,
  PracticeAssessmentData,
  PregnancyCoverageNextAction,
} from '../../types/assessments/assessment'
import { UserGroup } from '../../types/shared/db'
import { User } from '../../types/users/user'
import { NextAction } from '../../types/utils/common'
import { mergeData, parseNextActionDate } from '../data'
import { getDateString } from '../dates'
import { getCorrectedValue } from '../populated'

export const getPracticeAppointmentsCollectionPath = (midwifeId: string) =>
  `${MIDWIVES}/${midwifeId}/${APPOINTMENTS}`

// export const getPracticeVisitDraftsCollectionPath = (midwifeId: string) =>
//   `${MIDWIVES}/${midwifeId}/${VISIT_DRAFTS}`
export const getPracticeConsentFormsCollectionPath = (midwifeId: string) =>
  `${MIDWIVES}/${midwifeId}/${CONSENT_FORMS}`

export const getPracticeConsentFormPath = (midwifeId: string, consentFormId: string) =>
  `${getPracticeConsentFormsCollectionPath(midwifeId)}/${consentFormId}`

// Practice contract instances live under the practice they belong to; the signer is
// recorded on the doc (toBeSignedByUser).
export const getPracticeContractsCollectionPath = (practiceId: string) =>
  `${MIDWIVES}/${practiceId}/${PRACTICE_CONTRACTS}`

export const getPracticeContractPath = (practiceId: string, contractId: string) =>
  `${getPracticeContractsCollectionPath(practiceId)}/${contractId}`

export const getAssessmentName = (
  assessment: Assessment | PracticeAssessmentData | PopulatedAssessment | AssessmentSnippet | null,
  assessmentSnippet?: AssessmentSnippet,
  short?: boolean,
) => {
  const deliveredOn =
    (assessment as Assessment)?.delivery?.deliveredOn ??
    (assessment as PracticeAssessmentData)?.deliveredOn ??
    null

  const edd = assessmentSnippet?.edd ?? getCorrectedValue(assessment as Assessment, 'delivery.edd')
  const assessmentName =
    (assessment as Assessment)?.name ||
    (assessment as PracticeAssessmentData)?.assessmentName ||
    assessmentSnippet?.assessmentName
  const enteredName = assessmentName && assessmentName !== 'zzz' ? assessmentName : ''
  if (enteredName) return enteredName

  const option = getCorrectedValue(assessment as Assessment, 'insuranceInfo.option')
  if (option === 'not-pregnant') {
    const notPregnantOption = getCorrectedValue(
      assessment as Assessment,
      'insuranceInfo.notPregnantOption',
    )
    if (notPregnantOption === 'current-plan') return 'Not pregnant - coverage assessment'
    if (notPregnantOption) return 'Not pregnant - exploring options'
    return 'Not pregnant'
  }

  if (deliveredOn)
    return `${short ? 'DEL' : 'Delivered'}: ${getDateString(deliveredOn, 'short', true)}`
  return `EDD: ${edd ? getDateString(edd, 'short', true) : 'Awaiting'}`
}

export const getSnippetPartnerEmail = (user: User) =>
  user.partnerInfo?.corrections?.email ?? user.partnerInfo?.data?.email ?? null

export const getSnippetPartnerName = (user: User) =>
  user.partnerInfo?.corrections?.name ?? user.partnerInfo?.data?.name ?? null
export const getBlankAssessment = (
  createdBy: string,
  createdByGroup: UserGroup,
  patientId: string,
  midwifeId?: string | null,
): Assessment => ({
  archivedOn: null,
  createdOn: Date.now(),
  patientId,
  midwifeId: midwifeId ?? null,
  data: midwifeId ? { delivery: { midwifeId } } : {},
  delivery: null,
  primaryCoverageId: null,
  secondaryCoverageId: null,
  createdBy,
  createdByGroup,
})
export const getAssessmentFilesCollection = (
  firstArg: string,
  practiceId: string | undefined | null,
) => {
  switch (firstArg) {
    case 'files':
      return ASSESSMENTS
    case 'adminFiles':
      return ASSESSMENTS_ADMIN
    case 'practiceFiles':
      if (!practiceId) throw new Error('No practice ID provided when getting practice file')
      return getPracticeAssessmentCollectionPath(practiceId)
    default:
      throw new Error(`Invalid file id: ${firstArg} - should start with insuranceInfo or files`)
  }
}

export const getClosestNextAction = (nextActions: PregnancyCoverageNextAction[]) =>
  nextActions.reduce<NextAction | null>((acc, { nextAction: curr }) => {
    if (!acc) return curr ?? null
    if (!curr) return acc
    const date = parseNextActionDate(curr.text)
    if (!date) return acc
    if (date < parseNextActionDate(acc.text)) return curr ?? null
    return acc
  }, null)

export const getHasComplaints = (assessment: AdminAssessmentData) => {
  const { nextActions, gynNextAction } = assessment
  const nextActionTexts = [
    gynNextAction?.text,
    nextActions?.primaryCoverage?.text,
    nextActions?.secondaryCoverage?.text,
    ...Object.values(nextActions?.additionalPlans ?? {}).map(a => a?.text),
  ].filter(Boolean) as string[]
  return nextActionTexts.some(t => t?.toLowerCase()?.includes('complaint'))
}

export const getInitialSendDate = (assessment: Assessment): number | null => {
  const { sentOn, previousResults } = assessment
  if (previousResults?.length) {
    return Math.min(...(previousResults?.map(r => r.sentOn) ?? []))
  }
  return sentOn ?? null
}

export const mergeAssessmentData = (assessment?: Assessment) =>
  mergeData(assessment?.data ?? {}, assessment?.corrections ?? {})

const MS_PER_DAY = 86_400_000
const EDD_OFFSET_DAYS = 280

/**
 * Returns gestational age in days for a given encounter date and EDD (ms timestamp),
 * or null if the inputs are missing/invalid or the result would be non-positive.
 */
export const getGestationalAgeDays = (
  edd: number | null | undefined,
  encounterDate: string | null | undefined,
): number | null => {
  if (!edd || !encounterDate) return null
  const dateMs = new Date(encounterDate).getTime()
  if (isNaN(dateMs)) return null
  const days = Math.round((dateMs - (edd - EDD_OFFSET_DAYS * MS_PER_DAY)) / MS_PER_DAY)
  return days > 0 ? days : null
}
